import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useThemeColor } from '../../../hooks/useThemeColor';
import { useTranslation } from '../../../i18n/LanguageContext';
import axios from 'axios';
import { BASE_URL } from '../../../components/constants';
import { authHeaders } from '../../../utils/authHeaders';

const AddCategoryModal = ({ isOpen, onClose, existingCategories = [], taskId, onCategoriesChanged }) => {
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();

  const [categoryName, setCategoryName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCategoryName('');
      setError('');
    }
  }, [isOpen]);

  const handleAddCategory = async () => {
    const trimmedName = categoryName.trim();

    if (!trimmedName) {
      setError(t('agentSettings.addCategoryEmptyError'));
      return;
    }

    if (existingCategories.some(cat => cat.category_name === trimmedName)) {
      setError(t('agentSettings.addCategoryExistsError'));
      return;
    }

    if (!taskId) {
      setError(t('agentSettings.addCategoryNoAgentError'));
      return;
    }

    setIsAdding(true);
    setError('');

    try {
      await axios.post(`${BASE_URL}/categories`, {
        task_id: taskId,
        category_name: trimmedName
      }, authHeaders());
      setCategoryName('');
      if (onCategoriesChanged) await onCategoriesChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('agentSettings.addCategoryCreateError'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveCategory = async (categoryId) => {
    setDeletingId(categoryId);
    try {
      await axios.delete(`${BASE_URL}/categories/${categoryId}`, authHeaders());
      if (onCategoriesChanged) await onCategoriesChanged();
    } catch (err) {
      setError(t('agentSettings.addCategoryDeleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCategory();
    }
  };

  const handleClose = () => {
    setCategoryName('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-[560px] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6">
          <button
            onClick={handleClose}
            className="absolute top-6 right-6 p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <X size={20} className="text-[#6B7280]" />
          </button>

          <h2 className="text-[24px] font-semibold text-[#111827] text-center mb-3">
            {t('agentSettings.addCategoryTitle')}
          </h2>

          <p className="text-[14px] text-[#6B7280] text-center max-w-[420px] mx-auto">
            {t('agentSettings.addCategorySubtitle')}
          </p>
        </div>

        {/* Form Content */}
        <div className="px-8 pb-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[14px] text-red-600">
              {error}
            </div>
          )}

          {/* Category Name Input */}
          <div className="mb-5">
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">
              {t('agentSettings.addCategoryNameLabel')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                  setError('');
                }}
                onKeyDown={handleKeyDown}
                placeholder={t('agentSettings.addCategoryNamePlaceholder')}
                className="flex-1 px-4 py-3 text-[14px] text-[#374151] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[var(--focus-color)] transition-colors placeholder:text-[#9CA3AF]"
                style={{ '--focus-color': aispaceColor }}
                disabled={isAdding}
              />
            </div>
            <p className="text-[12px] text-[#9CA3AF] mt-2">
              {t('agentSettings.addCategoryEnterHint')}
            </p>
          </div>

          {/* Category Tags */}
          {existingCategories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {existingCategories.map((category) => (
                <span
                  key={category.id}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[14px] text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB] rounded-full"
                >
                  {category.category_name}
                  <button
                    onClick={() => handleRemoveCategory(category.id)}
                    disabled={deletingId === category.id}
                    className="hover:text-[#111827] transition-colors disabled:opacity-50"
                  >
                    {deletingId === category.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-[#F9FAFB] flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] transition-colors"
          >
            {t('agentSettings.cancel')}
          </button>
          <button
            disabled={isAdding || !categoryName.trim()}
            onClick={handleAddCategory}
            className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[160px] justify-center"
            style={{ backgroundColor: aispaceColor }}
          >
            {isAdding ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>{t('agentSettings.saving')}</span>
              </>
            ) : (
              <span>{t('agentSettings.addCategoryConfirm')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;
